import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  getFieldVisit, 
  deleteFieldVisit, 
  updateFieldTask, 
  deleteFieldTask,
  createFieldTask,
  getStaffMembers
} from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Edit,
  Trash2,
  MapPin,
  Calendar,
  Users,
  Building,
  MessageSquare,
  Package,
  Target,
  CheckCircle,
  Clock,
  AlertTriangle,
  ClipboardList,
  Plus,
  X,
  Play,
  Pause,
  Volume2
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

export default function FieldVisitDetail() {
  const navigate = useNavigate();
  const { visitId } = useParams();

  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTaskId, setDeleteTaskId] = useState(null);
  const [staff, setStaff] = useState([]);
  
  // Audio playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const audioPlayerRef = useRef(null);

  // New task dialog
  const [newTaskDialogOpen, setNewTaskDialogOpen] = useState(false);
  const [newTask, setNewTask] = useState({
    description: '',
    due_date: '',
    assigned_to: []
  });
  const [savingTask, setSavingTask] = useState(false);

  useEffect(() => {
    loadVisit();
    loadStaff();
  }, [visitId]);
  
  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
      }
    };
  }, []);

  const playAudio = () => {
    if (!visit?.audio_url) return;
    
    if (isPlaying && audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      setIsPlaying(false);
      return;
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const fullUrl = visit.audio_url.startsWith('http') ? visit.audio_url : `${backendUrl}${visit.audio_url}`;
    
    const audio = new Audio(fullUrl);
    audioPlayerRef.current = audio;
    setIsPlaying(true);
    
    audio.onended = () => setIsPlaying(false);
    audio.onerror = () => {
      toast.error('Failed to play recording');
      setIsPlaying(false);
    };
    
    audio.play().catch(err => {
      console.error('Audio playback error:', err);
      toast.error('Failed to play recording');
      setIsPlaying(false);
    });
  };

  const loadVisit = async () => {
    try {
      setLoading(true);
      const res = await getFieldVisit(visitId);
      setVisit(res.data);
    } catch (error) {
      toast.error('Failed to load visit details');
      navigate('/field-activity');
    } finally {
      setLoading(false);
    }
  };

  const loadStaff = async () => {
    try {
      const res = await getStaffMembers(true);
      setStaff(res.data);
    } catch (error) {
      console.error('Failed to load staff');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteFieldVisit(visitId);
      toast.success('Visit deleted successfully');
      navigate('/field-activity');
    } catch (error) {
      toast.error('Failed to delete visit');
    }
    setDeleteDialogOpen(false);
  };

  const handleTaskStatusChange = async (taskId, newStatus) => {
    try {
      await updateFieldTask(taskId, { status: newStatus });
      toast.success('Task status updated');
      loadVisit();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await deleteFieldTask(taskId);
      toast.success('Task deleted');
      loadVisit();
    } catch (error) {
      toast.error('Failed to delete task');
    }
    setDeleteTaskId(null);
  };

  const handleTaskAssigneeToggle = (staffId) => {
    setNewTask(prev => ({
      ...prev,
      assigned_to: prev.assigned_to.includes(staffId)
        ? prev.assigned_to.filter(id => id !== staffId)
        : [...prev.assigned_to, staffId]
    }));
  };

  const handleCreateTask = async () => {
    if (!newTask.description.trim()) {
      toast.error('Please enter a task description');
      return;
    }
    if (!newTask.due_date) {
      toast.error('Please select a due date');
      return;
    }

    setSavingTask(true);
    try {
      await createFieldTask({
        visit_id: visitId,
        buyer_id: visit.buyer_id,
        description: newTask.description,
        due_date: newTask.due_date,
        assigned_to: newTask.assigned_to
      });
      toast.success('Task created successfully');
      setNewTaskDialogOpen(false);
      setNewTask({ description: '', due_date: '', assigned_to: [] });
      loadVisit();
    } catch (error) {
      toast.error('Failed to create task');
    } finally {
      setSavingTask(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800';
      case 'Pending':
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const isOverdue = (dueDate, status) => {
    if (status === 'Completed') return false;
    const today = new Date().toISOString().split('T')[0];
    return dueDate < today;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-maroon-600"></div>
      </div>
    );
  }

  if (!visit) {
    return null;
  }

  return (
    <div className="space-y-4" data-testid="field-visit-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/field-activity')}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-maroon-900">{visit.buyer_name}</h1>
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {visit.geography_market}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/field-activity/edit/${visitId}`}>
            <Button variant="outline" size="sm" data-testid="edit-visit-btn">
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          </Link>
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50"
            onClick={() => setDeleteDialogOpen(true)}
            data-testid="delete-visit-btn"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </div>
      </div>

      {/* Visit Info Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base lg:text-lg flex items-center gap-2">
            <Calendar className="h-4 w-4 text-maroon-600" />
            Visit Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Visit Date</p>
              <p className="font-medium">{formatDate(visit.visit_date)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Geography/Market</p>
              <p className="font-medium flex items-center gap-1">
                <MapPin className="h-4 w-4 text-gray-400" />
                {visit.geography_market}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Buyer</p>
              <p className="font-medium flex items-center gap-1">
                <Building className="h-4 w-4 text-gray-400" />
                {visit.buyer_name}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Staff Participants</p>
              <div className="flex flex-wrap gap-1">
                {visit.staff_names?.length > 0 ? (
                  visit.staff_names.map((name, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">
                      {name}
                    </Badge>
                  ))
                ) : (
                  <span className="text-gray-400 text-sm">None specified</span>
                )}
              </div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t text-xs text-gray-400">
            <p>Created by {visit.created_by} on {formatDateTime(visit.created_at)}</p>
          </div>
        </CardContent>
      </Card>

      {/* Voice Recording Card */}
      {visit.audio_url && (
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base lg:text-lg flex items-center gap-2">
              <Volume2 className="h-4 w-4 text-blue-600" />
              Voice Recording
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <p className="text-sm text-blue-700">
                Original voice recording from this visit
              </p>
              <Button
                onClick={playAudio}
                className={isPlaying ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"}
                size="sm"
                data-testid="play-audio-btn"
              >
                {isPlaying ? (
                  <>
                    <Pause className="h-4 w-4 mr-1" />
                    Pause
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-1" />
                    Play Recording
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Discussion Notes */}
      {visit.discussion_notes && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base lg:text-lg flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-maroon-600" />
              Discussion Notes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{visit.discussion_notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Samples Shown */}
      {visit.samples_shown && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base lg:text-lg flex items-center gap-2">
              <Package className="h-4 w-4 text-maroon-600" />
              Samples/Items Shown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{visit.samples_shown}</p>
          </CardContent>
        </Card>
      )}

      {/* Potential Items */}
      {visit.potential_items?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base lg:text-lg flex items-center gap-2">
              <Target className="h-4 w-4 text-maroon-600" />
              Potential Items ({visit.potential_items.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {visit.potential_items.map((item, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-sm">{item.item_description}</p>
                  {item.notes && (
                    <p className="text-xs text-gray-500 mt-1">{item.notes}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tasks */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base lg:text-lg flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-maroon-600" />
              Follow-up Tasks ({visit.tasks?.length || 0})
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setNewTaskDialogOpen(true)}
              data-testid="add-task-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Task
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!visit.tasks || visit.tasks.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <ClipboardList className="h-10 w-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No follow-up tasks created</p>
            </div>
          ) : (
            <div className="space-y-2">
              {visit.tasks.map((task) => (
                <div
                  key={task.id}
                  className={`p-3 rounded-lg border ${
                    isOverdue(task.due_date, task.status)
                      ? 'border-red-200 bg-red-50'
                      : task.status === 'Completed'
                      ? 'border-green-200 bg-green-50'
                      : 'border-gray-200 bg-white'
                  }`}
                  data-testid={`task-${task.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${task.status === 'Completed' ? 'line-through text-gray-500' : ''}`}>
                        {task.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <Badge className={`text-xs ${getStatusColor(task.status)}`}>
                          {task.status}
                        </Badge>
                        <span className={`text-xs flex items-center gap-1 ${
                          isOverdue(task.due_date, task.status) ? 'text-red-600 font-medium' : 'text-gray-500'
                        }`}>
                          {isOverdue(task.due_date, task.status) ? (
                            <AlertTriangle className="h-3 w-3" />
                          ) : (
                            <Clock className="h-3 w-3" />
                          )}
                          Due: {formatDate(task.due_date)}
                        </span>
                        {task.assigned_names?.length > 0 && (
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {task.assigned_names.join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {task.status !== 'Completed' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => handleTaskStatusChange(task.id, 'Completed')}
                          title="Mark as completed"
                          data-testid={`complete-task-${task.id}`}
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteTaskId(task.id)}
                        title="Delete task"
                        data-testid={`delete-task-${task.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Visit Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Visit</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this visit? This will also delete all associated tasks.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Task Dialog */}
      <AlertDialog open={!!deleteTaskId} onOpenChange={() => setDeleteTaskId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this task? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteTask(deleteTaskId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New Task Dialog */}
      <Dialog open={newTaskDialogOpen} onOpenChange={setNewTaskDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Follow-up Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="task_desc" className="mb-1 block">
                Task Description *
              </Label>
              <Input
                id="task_desc"
                placeholder="What needs to be done?"
                value={newTask.description}
                onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                data-testid="new-task-description"
              />
            </div>
            <div>
              <Label htmlFor="task_due" className="mb-1 block">
                Due Date *
              </Label>
              <Input
                id="task_due"
                type="date"
                value={newTask.due_date}
                onChange={(e) => setNewTask(prev => ({ ...prev, due_date: e.target.value }))}
                data-testid="new-task-due-date"
              />
            </div>
            <div>
              <Label className="mb-2 block">Assign To</Label>
              <div className="flex flex-wrap gap-2">
                {staff.map((member) => (
                  <label
                    key={member.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                      newTask.assigned_to.includes(member.id)
                        ? 'bg-blue-100 border-blue-500 text-blue-800'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <Checkbox
                      checked={newTask.assigned_to.includes(member.id)}
                      onCheckedChange={() => handleTaskAssigneeToggle(member.id)}
                    />
                    <span className="text-sm">{member.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewTaskDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateTask}
              disabled={savingTask}
              className="bg-maroon-600 hover:bg-maroon-700"
              data-testid="save-new-task-btn"
            >
              {savingTask ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
